import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  Button,
  Container,
  Paper,
  Table,
  TableBody,
  TableContainer,
  Icon,
} from '@material-ui/core';
import clsx from 'clsx';
import { v4 as uuid } from 'uuid';

import { PaginationReturn } from 'generated';
import { strNormalize } from 'utils';

import { CustomLink, CustomTooltip, ItemHeader } from '..';
import CustomPagination from './CustomPagination';
import EnhancedTableHead from './EnhancedTableHead';
import { exportToXLSX, getComparator, stableSort } from './functions';
import { useStyles } from './styles';
import TableRowData from './TableRowData';
import {
  CompareFn,
  DefineColumn,
  FilterItems,
  GenericObject,
  Order,
  SearchParams,
} from './types';

export type PaginationPosition = 'top' | 'bottom' | 'both';
export type PaginationAlignment = 'left' | 'center' | 'right';

interface DataTableProps {
  textLabel?: ReactNode;
  noMarginTop?: boolean;
  secondLineHeader?: ReactNode;
  defineColumns: DefineColumn[];
  rowsData: any;
  loading: boolean;
  context?: any;
  pagination?: PaginationReturn;
  paginationPosition?: PaginationPosition;
  paginationAlignment?: PaginationAlignment;
  rowSelectable?: boolean;
  rowKeyTitle?: string;
  filterListData?: FilterItems[];
  SubHeader?: React.FC<any> | React.ComponentType;
  reloadTrigger?: string;
  hiddenColumns?: string[];
  height?: number | string;
  exportToExcel?: boolean;
  exportToExcelButtonIsDisabled?: boolean;
  exportToExcelHiddenColumns?: string[];
  exportToExcelHeaders?: Partial<DefineColumn>[];
  filenamePrefix?: string;
  containerNoPadding?: boolean;
  containerNoPaddingTop?: boolean;
  hasURLSearchParams?: boolean;
  filterType?: any;
  externalSearch?: SearchParams;
  onRowSelect?(row: any): void;
  onSearch?(searchParams: SearchParams): void;
  upSearch?(value: SearchParams): void;
  onPagination?(value: number): void;
  onChangeItemHeader?(name: string): void;
  dataTransformToExcel?: (row: any) => any;
  rowsDataTransform?(rowsData: any[]): any[];
  TopEndContainerChildren?: ReactNode;
  perPage: number;
}

type MultiProps = DataTableProps;

export const DataTable: React.FC<MultiProps> = ({
  textLabel,
  defineColumns,
  rowsData,
  filterListData,
  loading,
  SubHeader,
  reloadTrigger,
  onChangeItemHeader,
  rowSelectable,
  rowKeyTitle,
  onRowSelect,
  onSearch,
  upSearch,
  hiddenColumns,
  context,
  exportToExcel,
  exportToExcelHeaders,
  exportToExcelHiddenColumns,
  dataTransformToExcel,
  filenamePrefix,
  pagination,
  onPagination,
  containerNoPadding,
  containerNoPaddingTop,
  secondLineHeader,
  rowsDataTransform,
  exportToExcelButtonIsDisabled,
  TopEndContainerChildren,
  filterType,
  externalSearch,
  perPage,
  noMarginTop = false,
  hasURLSearchParams = true,
  paginationAlignment = 'right',
  paginationPosition = 'bottom',
  height = '50vh',
}: MultiProps) => {
  const classes = useStyles();

  const tableRef = useRef<HTMLDivElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [rows, setRows] = useState(rowsData);
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<string>('');
  const [oldReloadTrigger, setOldReloadTrigger] = useState<string | undefined>(
    reloadTrigger,
  );
  const [searchParams, setSearchParams] = useState<SearchParams | undefined>(
    externalSearch,
  );
  const [columnCompareFunction, setColumnCompareFunction] = useState<
    CompareFn | undefined
  >();

  const filteredDefineColumns = useMemo(() => {
    if (hiddenColumns && hiddenColumns.length) {
      return defineColumns.filter(column => !hiddenColumns.includes(column.id));
    }

    return defineColumns;
  }, [defineColumns, hiddenColumns]);

  const paginationDescribe = useMemo(() => {
    if (pagination) {
      let to = pagination.currentPage * perPage;
      to = to > pagination.totalCount ? pagination.totalCount : to;

      return `${(pagination.currentPage - 1) * perPage + 1} até ${to}`;
    }

    return 'N/A';
  }, [pagination, perPage]);

  const handleSort = useCallback(
    (property: string, colCompareFn?: CompareFn) => {
      const isAsc = orderBy === property && order === 'asc';
      setColumnCompareFunction(
        colCompareFn ? () => colCompareFn : colCompareFn,
      );
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(property);
    },
    [order, orderBy],
  );

  const handleSelect = useCallback(
    (event: React.MouseEvent<unknown>, name: string) => {
      event.preventDefault();

      if (onChangeItemHeader) {
        onChangeItemHeader(name);
      }
    },
    [onChangeItemHeader],
  );

  const handleSearch = useCallback(
    (value: string, searchParam: number | string, fromUrl = false) => {
      let composeSearchParams: SearchParams =
        fromUrl && hasURLSearchParams
          ? { ...searchParams }
          : {
              ...searchParams,
              [searchParam]: strNormalize(value.toLowerCase()),
            };
      composeSearchParams = Object.entries<string>(composeSearchParams).reduce(
        (accumulator: SearchParams, [key, searchValue]) => {
          if (searchValue) {
            accumulator[key] = searchValue;
          }

          return accumulator;
        },
        {},
      );

      const composeSearchHasValues = !!Object.values<string>(
        composeSearchParams,
      ).filter(searchValue => !!searchValue).length;

      if (onSearch) {
        onSearch(composeSearchParams);
        return;
      }

      const filteredRows = rowsData.filter((row: any) => {
        const composeRow = Object.entries<string>(
          row as { [key: string]: string },
        )
          .map(([key, rowValue]) => [
            key,
            strNormalize(JSON.stringify(rowValue)).toLowerCase(),
          ])
          .filter(([_, cellValue]) => cellValue !== 'undefined');

        const searchParamsLength = Object.keys(composeSearchParams).length;
        const rowContains = !!(
          composeRow.filter(([key, rowValue]) =>
            rowValue.includes(composeSearchParams[key]),
          ).length === searchParamsLength
        );

        return rowContains;
      });

      setRows(composeSearchHasValues ? filteredRows : rowsData);

      if (composeSearchParams && upSearch) {
        upSearch(composeSearchParams);
      }

      setSearchParams(composeSearchParams);
    },
    [searchParams, onSearch, upSearch, rowsData, hasURLSearchParams],
  );

  const compareFn = useCallback(
    (o: Order, ob: string) => {
      if (columnCompareFunction) {
        const fn = columnCompareFunction(o, ob);

        return fn || getComparator(o, ob);
      }

      return getComparator(o, ob);
    },
    [columnCompareFunction],
  );

  const handleExportExcel = useCallback(() => {
    const formattedRows = rowsDataTransform ? rowsDataTransform(rows) : rows;

    exportToXLSX(
      exportToExcelHeaders || defineColumns,
      formattedRows,
      {
        dataTransform: dataTransformToExcel,
        excludeValues: exportToExcelHiddenColumns,
      },
      filenamePrefix,
    );
  }, [
    rowsDataTransform,
    rows,
    exportToExcelHeaders,
    defineColumns,
    dataTransformToExcel,
    exportToExcelHiddenColumns,
    filenamePrefix,
  ]);

  const handlePagination = useCallback(
    (newPage: number) => {
      if (onPagination) {
        onPagination(newPage);
      }

      tableRef.current?.scrollTo(0, 0);
    },
    [onPagination],
  );

  useEffect(() => {
    if (reloadTrigger !== oldReloadTrigger) {
      setOldReloadTrigger(reloadTrigger);
      setRows(rowsData);
    }

    return () => {
      setSearchParams({});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadTrigger, rowsData, oldReloadTrigger, hiddenColumns]);

  return (
    <div
      className={clsx(classes.tableData, {
        [classes.noMarginTop]: noMarginTop,
      })}
    >
      {textLabel && (
        <ItemHeader
          containerNoPadding={containerNoPadding}
          textLabel={textLabel}
          secondLineHeader={secondLineHeader}
        >
          {filterListData && filterListData.length > 0 && <>Exibindo: </>}
          {filterListData?.map(item => (
            <CustomLink
              style={{ textTransform: 'capitalize' }}
              key={item.value}
              selected={filterType?.id === item.value ? 'true' : 'false'}
              variant="button"
              onClick={(event: React.MouseEvent) =>
                handleSelect(event, item.value)
              }
            >
              {item.name}
            </CustomLink>
          ))}
        </ItemHeader>
      )}

      {SubHeader && (
        <div
          className={clsx(classes.subHeader, {
            [classes.noPadding]: containerNoPadding,
          })}
        >
          <SubHeader />
        </div>
      )}

      <>
        <div className={classes.content}>
          <Container
            style={{
              position: 'relative',
            }}
            className={clsx({
              [classes.noPadding]: containerNoPadding,
              [classes.noPaddingTop]: containerNoPaddingTop,
            })}
          >
            {exportToExcel && (
              <div className={classes.exportContainer}>
                {TopEndContainerChildren}
                <CustomTooltip
                  ref={tooltipRef}
                  labelValue="Exportar dados para EXCEL"
                  showCircle={false}
                  showTitle={false}
                >
                  <Button
                    color="primary"
                    size="small"
                    type="button"
                    disabled={exportToExcelButtonIsDisabled}
                    onClick={handleExportExcel}
                  >
                    <Icon color="inherit" style={{ fontSize: 24 }}>
                      get_app
                    </Icon>
                  </Button>
                </CustomTooltip>
              </div>
            )}
            {pagination &&
              (paginationPosition === 'both' || paginationPosition === 'top') &&
              Array.isArray(rowsData) && (
                <CustomPagination
                  loading={loading}
                  actualPage={pagination.currentPage - 1}
                  pageCount={pagination.totalPage}
                  paginationDescribe={paginationDescribe}
                  paginationAlignment={paginationAlignment}
                  paginationPosition={paginationPosition}
                  onPageChange={handlePagination}
                  totalRows={pagination.totalCount}
                />
              )}
            <TableContainer
              component={Paper}
              style={{ maxHeight: height }}
              ref={tableRef}
            >
              <Table
                stickyHeader
                className={classes.table}
                aria-labelledby="tabletitle"
                aria-label="enhanced table"
              >
                <EnhancedTableHead
                  defineColumns={filteredDefineColumns}
                  order={order}
                  orderBy={orderBy}
                  onSort={handleSort}
                  onSearch={handleSearch}
                  reloadTrigger={oldReloadTrigger}
                  hiddenColumns={hiddenColumns}
                  searchParams={searchParams}
                />
                {!loading && !rowsData.length ? (
                  <div className={classes.loadingContainer}>
                    <h2>Nenhum dado encontrado para essa pesquisa</h2>
                    <Icon className={classes.icon} fontSize="large">
                      sentiment_very_dissatisfied
                    </Icon>
                  </div>
                ) : (
                  <TableBody className={classes.tableBody}>
                    {stableSort<GenericObject>(
                      loading
                        ? Array(6)
                            .fill(0)
                            .map((_, index) => index)
                        : rows,
                      compareFn(order, orderBy),
                    ).map((row, index) => (
                      <TableRowData
                        key={uuid()}
                        index={index}
                        row={row}
                        rows={rows}
                        onRowClick={onRowSelect}
                        defineColumns={defineColumns}
                        hiddenColumns={hiddenColumns}
                        selectable={rowSelectable}
                        rowKeyTitle={rowKeyTitle}
                        context={context}
                        loading={loading}
                      />
                    ))}
                  </TableBody>
                )}
              </Table>
            </TableContainer>
            {pagination &&
              !!rowsData.length &&
              (paginationPosition === 'both' ||
                paginationPosition === 'bottom') &&
              Array.isArray(rowsData) && (
                <CustomPagination
                  loading={loading}
                  actualPage={pagination.currentPage}
                  pageCount={pagination.totalPage}
                  paginationDescribe={paginationDescribe}
                  paginationAlignment={paginationAlignment}
                  paginationPosition={paginationPosition}
                  onPageChange={handlePagination}
                  totalRows={pagination.totalCount}
                />
              )}
          </Container>
        </div>
      </>
    </div>
  );
};
